import { Interceptor } from '@utilitywarehouse/tracking-core';

import {
  PsuPagePartnerReferralEntity,
  psuPagePartnerReferralEntity,
} from './trackingEvents';

// Global context data that can be updated throughout the app
let globalPartnerReferralContext: PsuPagePartnerReferralEntity | null = null;

/**
 * Updates the global partner referral context that will be automatically
 * added to all tracking events as a context entity
 */
export const updatePartnerReferralContext = (
  context: PsuPagePartnerReferralEntity,
) => {
  globalPartnerReferralContext = { ...context };
};

/**
 * Clears the global partner referral context
 */
export const clearPartnerReferralContext = () => {
  globalPartnerReferralContext = null;
};

/**
 * Gets the current partner referral context
 */
export const getPartnerReferralContext =
  (): PsuPagePartnerReferralEntity | null => {
    return globalPartnerReferralContext;
  };

/**
 * Interceptor that automatically adds the partner referral context entity
 * to all tracking events when it's available
 */
export const partnerReferralContextInterceptor: Interceptor = (payload) => {
  // Only add context if we have partner referral data
  if (!globalPartnerReferralContext) {
    return payload;
  }

  // Initialize contexts array if it doesn't exist
  const contexts = payload.options?.contexts ?? [];

  // Create the partner referral context entity using the existing function
  const partnerReferralContextEntity = psuPagePartnerReferralEntity(
    globalPartnerReferralContext,
  );

  // Check if partner referral context already exists to avoid duplicates
  const hasPartnerReferralContext = contexts.some(
    (ctx) =>
      typeof ctx === 'object' &&
      ctx !== null &&
      'name' in ctx &&
      ctx.name === 'psu_page_partner_referral',
  );

  // Add the partner referral context if it doesn't already exist
  if (!hasPartnerReferralContext) {
    // Initialize payload.options if it doesn't exist
    payload.options = payload.options ?? {};
    payload.options.contexts = [...contexts, partnerReferralContextEntity];
  }

  return payload;
};

/**
 * Utility function to generate a unique instance ID for partner referrals
 */
export const generateUniqueInstanceId = (
  leadCode: string,
  positionId: string,
): string => {
  const timestamp = Date.now();
  return `${leadCode}_${positionId}_${timestamp}`;
};
